import { useState } from 'react';
import { GraduationCap, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useTheme } from '@/context/ThemeContext';
import COUNTRIES from '@/data/countries';

const EducationConsultingPage = () => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    current_education_level: '',
    desired_field: '',
    target_countries: '',
    budget_range: '',
    preferred_start_date: '',
    language_proficiency: '',
    additional_info: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/service-requests', {
        ...formData,
        service_category: 'education_consulting',
        created_at: new Date().toISOString(),
      });
      
      toast.success('درخواست مشاوره شما ثبت شد. مشاور ما به زودی با شما تماس خواهد گرفت.');
      
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        current_education_level: '',
        desired_field: '',
        target_countries: '',
        budget_range: '',
        preferred_start_date: '',
        language_proficiency: '',
        additional_info: '',
      });
    } catch (error) {
      toast.error('خطا در ثبت درخواست. لطفاً دوباره تلاش کنید.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen py-12 ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
            isDark ? 'bg-[#D4AF37]/10' : 'bg-[#D4AF37]/20'
          }`}>
            <GraduationCap className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <h1 className={`text-4xl sm:text-5xl font-light mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            مشاوره مهاجرت تحصیلی
          </h1>
          <p className={`text-lg ${
            isDark ? 'text-zinc-400' : 'text-gray-600'
          }`}>
            راهنمایی کامل برای انتخاب بهترین کشور، دانشگاه و رشته تحصیلی
          </p>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {[
            { title: 'مشاوره اختصاصی', desc: 'مشاوره یک‌به‌یک با متخصصان مجرب' },
            { title: 'بررسی سوابق', desc: 'تحلیل کامل سوابق تحصیلی و کاری شما' },
            { title: 'انتخاب بهترین مسیر', desc: 'پیشنهاد بهترین گزینه‌ها متناسب با شرایط شما' },
          ].map((item) => (
            <div key={item.title} className={`border rounded-xl p-6 text-center ${
              isDark 
                ? 'glass-effect border-white/10' 
                : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <Check className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
              <h3 className={`font-medium mb-2 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>{item.title}</h3>
              <p className={`text-sm ${
                isDark ? 'text-zinc-400' : 'text-gray-600'
              }`}>{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className={`border rounded-2xl p-8 ${
          isDark 
            ? 'glass-effect border-white/10' 
            : 'bg-white border-gray-200 shadow-lg'
        }`}>
          <h2 className={`text-2xl font-light mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>فرم درخواست مشاوره</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>نام و نام خانوادگی *</label>
                <input
                  type="text"
                  name="full_name"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white placeholder:text-zinc-500'
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400'
                  }`}
                />
              </div>
              
              <div>
                <label className={`block text-sm mb-2 ${
                  isDark ? 'text-zinc-300' : 'text-gray-700'
                }`}>شماره تماس *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white placeholder:text-zinc-500'
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400'
                  }`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>ایمیل *</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  isDark
                    ? 'bg-white/5 border-white/10 text-white placeholder:text-zinc-500'
                    : 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>سطح تحصیلات فعلی *</label>
              <select
                name="current_education_level"
                required
                value={formData.current_education_level}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 font-medium focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  isDark
                    ? 'bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-[#D4AF37]/30 text-[#D4AF37]'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>انتخاب کنید</option>
                <option value="diploma" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>دیپلم</option>
                <option value="associate" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>کاردانی</option>
                <option value="bachelor" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>کارشناسی</option>
                <option value="master" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>کارشناسی ارشد</option>
                <option value="phd" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>دکترا</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>رشته تحصیلی موردنظر *</label>
              <input
                type="text"
                name="desired_field"
                required
                placeholder="مثلاً: مهندسی کامپیوتر، پزشکی، مدیریت و..."
                value={formData.desired_field}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  isDark
                    ? 'bg-white/5 border-white/10 text-white placeholder:text-zinc-500'
                    : 'bg-gray-50 border-gray-300 text-gray-900 placeholder:text-gray-400'
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm mb-2 ${
                isDark ? 'text-zinc-300' : 'text-gray-700'
              }`}>کشورهای موردنظر *</label>
              <select
                name="target_countries"
                required
                value={formData.target_countries}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-3 font-medium focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  isDark
                    ? 'bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-[#D4AF37]/30 text-[#D4AF37]'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="" className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>انتخاب کنید</option>
                {COUNTRIES.map((country) => (
                  <option key={country} value={country} className={isDark ? 'bg-[#1a1a1a] text-white' : 'bg-white text-gray-900'}>
                    {country}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-black font-medium py-4 rounded-lg hover:shadow-lg hover:shadow-[#D4AF37]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'در حال ارسال...' : 'ارسال درخواست مشاوره'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EducationConsultingPage;